import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, X } from "lucide-react";
import { useCopy } from "../../context/CopyContext";

const SignUpView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getCopy } = useCopy();
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    day: "",
    month: "",
    year: "",
    mobilePhone: "",
    marketingConsent: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get email from URL params if passed from QR code
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({
        ...prev,
        email: emailParam,
        confirmEmail: emailParam
      }));
    }
  }, [searchParams]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock sign up logic
    console.log("Sign up attempt:", formData);
    alert("Account created successfully! (Mock)");
  };

  const handleSocialLogin = (provider: string) => {
    console.log(`${provider} signup attempt`);
    alert(`${provider} account created successfully! (Mock)`);
  };

  const handleClose = () => {
    navigate('/sign-in');
  };

  // Generate arrays for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  return (
    <div className="min-h-screen bg-brand-background">
      {/* Mobile Container */}
      <div className="max-w-md mx-auto min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-brand-secondary text-brand-background p-4 flex items-center justify-between">
          <img 
            src="/brand-wob.png" 
            alt="Brand Logo" 
            className="h-8"
          />
          <button 
            onClick={handleClose}
            className="text-brand-background hover:text-brand-text-muted"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl font-semibold mb-2">Create Account</h1>
            <p className="text-sm text-brand-text-secondary mb-6">
              Sign up with social media or fill the form below and enjoy the benefits of having an account.
            </p>

            {/* Social Login Buttons */}
            <div className="space-y-3 mb-6">
              <button
                onClick={() => handleSocialLogin('Facebook')}
                className="w-full bg-[#1877F2] text-white py-3 flex items-center justify-center space-x-2 hover:bg-[#166FE5] transition-colors"
              >
                <span className="text-sm font-medium">Facebook</span>
              </button>
              <button
                onClick={() => handleSocialLogin('Google')}
                className="w-full bg-[#4285F4] text-white py-3 flex items-center justify-center space-x-2 hover:bg-[#3367D6] transition-colors"
              >
                <span className="text-sm font-medium">Google</span>
              </button>
            </div>

            <p className="text-xs text-brand-text-secondary mb-6">
              When you use Facebook or Google to login to our site, be advised that your data is governed by 
              Facebook's or Google's privacy policy and terms of use.
            </p>

            <div className="text-center mb-6">
              <span className="text-sm font-medium">Or</span>
            </div>

            <p className="text-sm text-brand-text-secondary mb-4">
              required <span className="text-red-500">(*)</span>
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name*"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name*"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>

              {/* Email Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email*"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                    required
                    autoComplete="email"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="confirmEmail"
                    placeholder="Confirm email*"
                    value={formData.confirmEmail}
                    onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password*"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm pr-10 font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff size={16} className="text-brand-text-muted" />
                    ) : (
                      <Eye size={16} className="text-brand-text-muted" />
                    )}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm Password*"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm pr-10 font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={16} className="text-brand-text-muted" />
                    ) : (
                      <Eye size={16} className="text-brand-text-muted" />
                    )}
                  </button>
                </div>
              </div>

              {/* Date of Birth */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Date of birth</h3>
                <p className="text-xs text-brand-text-secondary">
                  We want to wish you a happy birthday! When you provide your birthday, we will also use this 
                  information to personalize our marketing efforts
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <select
                    value={formData.day}
                    onChange={(e) => handleInputChange('day', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                  >
                    <option value="">Day</option>
                    {days.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <select
                    value={formData.month}
                    onChange={(e) => handleInputChange('month', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                  >
                    <option value="">Month</option>
                    {months.map((month, index) => (
                      <option key={month} value={index + 1}>{month}</option>
                    ))}
                  </select>
                  <select
                    value={formData.year}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                    className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                    style={{ fontFamily: 'var(--brand-font-secondary)' }}
                  >
                    <option value="">Year</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Mobile Phone */}
              <div>
                <input
                  type="tel"
                  placeholder="Mobile Phone"
                  value={formData.mobilePhone}
                  onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                  className="w-full px-3 py-3 bg-brand-background border border-brand-border focus:border-brand-text-primary focus:outline-none text-sm placeholder-brand-text-muted font-brand-secondary"
                  style={{ fontFamily: 'var(--brand-font-secondary)' }}
                />
              </div>

              {/* Marketing Consent */}
              <div className="space-y-3">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.marketingConsent}
                    onChange={(e) => handleInputChange('marketingConsent', e.target.checked)}
                    className="mt-1 flex-shrink-0"
                  />
                  <div className="text-xs text-brand-text-secondary">
                    <p>
                      {getCopy('signUp.marketingConsentText')}
                    </p>
                  </div>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-brand-secondary text-brand-background py-3 hover:bg-brand-tertiary transition-colors text-sm font-medium mt-6"
              >
                CREATE ACCOUNT
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SignUpView;
